import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Card, LoaderComponent, Modal, Button } from '../components/ui';
import { fmtInstante } from '../utils/tempoCv';
import {
  FiWatch, FiPlus, FiLock, FiUnlock, FiRefreshCw,
  FiCheckCircle, FiAlertCircle, FiInfo, FiActivity,
} from 'react-icons/fi';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Wristband {
  id: number;
  nfc_uid: string;
  label: string | null;
  status: 'ACTIVE' | 'BLOCKED' | 'INACTIVE';
  activated_at: string | null;
  saldo_disponivel: number;
  saldo_bonus: number;
}

interface ActivationResult {
  wristband_id: number;
  nfc_uid: string;
  saldo_atual: number;
  bonus_creditado?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fCVE = (v?: number | null) =>
  v == null ? '—' : new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(v);

const fDate = (d?: string | null) => {
  if (!d) return '—';
  return fmtInstante(d, { day: '2-digit', month: 'short', year: 'numeric' }, 'pt-PT');
};

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = {
    ACTIVE:   { label: 'Activa',    cls: 'bg-emerald-100 text-emerald-700' },
    BLOCKED:  { label: 'Bloqueada', cls: 'bg-red-100 text-red-700' },
    INACTIVE: { label: 'Inactiva',  cls: 'bg-gray-100 text-gray-500' },
  }[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500' };

  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

// ─── Activate Modal ────────────────────────────────────────────────────────────

interface ActivateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: ActivationResult) => void;
}

function ActivateModal({ open, onClose, onSuccess }: ActivateModalProps) {
  const { showError } = useToast();
  const [nfcUid, setNfcUid] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const uid = nfcUid.trim().toUpperCase();
    if (!uid) { setError('Introduz o UID da pulseira'); return; }

    setSaving(true);
    setError('');
    try {
      const res = await api.post<{ success: boolean; data?: ActivationResult; error?: string }>(
        '/user-events/cashless/wristbands/activate',
        { nfc_uid: uid, ...(label.trim() ? { label: label.trim() } : {}) }
      );
      if (res.data.success && res.data.data) {
        onSuccess(res.data.data);
        setNfcUid('');
        setLabel('');
        onClose();
      } else {
        setError(res.data.error || 'Erro ao activar pulseira');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao activar pulseira');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Associar Pulseira NFC"
      footer={
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <><FiRefreshCw className="animate-spin mr-1" /> A activar…</> : 'Activar Pulseira'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Instruções */}
        <div className="bg-blue-50 rounded-xl p-4 space-y-2">
          <h4 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
            <FiInfo /> Como encontrar o UID da pulseira
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Na app Pagali Pass (mobile) → Pulseira → aproxima ao telemóvel → copia o UID</li>
            <li>• Na etiqueta física da pulseira (formato: AA:BB:CC:DD)</li>
            <li>• Num leitor NFC do evento (o staff pode fornecer)</li>
          </ul>
        </div>

        <div>
          <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
            UID da Pulseira NFC <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={nfcUid}
            onChange={e => setNfcUid(e.target.value.toUpperCase())}
            placeholder="Ex: AA:BB:CC:DD ou AABBCCDD"
            className="w-full px-4 py-3 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none font-mono"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-pagali-gray-700 mb-1">
            Nome (opcional)
          </label>
          <input
            type="text"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Ex: Pulseira Mindelo Mágico 2026"
            className="w-full px-4 py-3 border border-pagali-gray-300 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary outline-none"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            <FiAlertCircle className="flex-shrink-0" /> {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Wristband Card ───────────────────────────────────────────────────────────

interface WristbandCardProps {
  w: Wristband;
  onToggleBlock: (w: Wristband) => void;
  blocking: boolean;
}

function WristbandCard({ w, onToggleBlock, blocking }: WristbandCardProps) {
  return (
    <div className="rounded-2xl border border-pagali-gray-200 bg-white overflow-hidden">
      {/* Header gradient */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-600 p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <FiWatch className="text-2xl" />
          <StatusBadge status={w.status} />
        </div>
        <p className="font-mono text-sm tracking-widest opacity-80">{w.nfc_uid}</p>
        <p className="font-semibold text-lg mt-1">{w.label || 'Pulseira NFC'}</p>
      </div>

      {/* Balance */}
      <div className="px-5 py-4 border-b border-pagali-gray-100">
        <p className="text-xs text-pagali-gray-500 mb-0.5">Saldo disponível</p>
        <p className="text-2xl font-bold text-pagali-gray-900">{fCVE(w.saldo_disponivel)}</p>
        {(w.saldo_bonus ?? 0) > 0 && (
          <p className="text-xs text-emerald-600 mt-0.5">+ {fCVE(w.saldo_bonus)} bónus</p>
        )}
      </div>

      {/* Meta + actions */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="text-xs text-pagali-gray-400">
          Activada em {fDate(w.activated_at)}
        </div>
        <button
          onClick={() => onToggleBlock(w)}
          disabled={blocking}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
            w.status === 'ACTIVE'
              ? 'bg-red-50 text-red-600 hover:bg-red-100'
              : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
          } disabled:opacity-50`}
        >
          {blocking
            ? <FiRefreshCw className="animate-spin" />
            : w.status === 'ACTIVE' ? <FiLock /> : <FiUnlock />}
          {w.status === 'ACTIVE' ? 'Bloquear' : 'Desbloquear'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const Pulseira: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [wristbands, setWristbands] = useState<Wristband[]>([]);
  const [loading, setLoading] = useState(true);
  const [activateOpen, setActivateOpen] = useState(false);
  const [blocking, setBlocking] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Pulseira NFC — Pagali';
    sessionStorage.setItem('pageTitle', 'Pulseira NFC');
    load();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data?: Wristband[] }>(
        '/user-events/cashless/wristbands'
      );
      setWristbands(res.data.success && Array.isArray(res.data.data) ? res.data.data : []);
    } catch {
      setWristbands([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleActivated = (result: ActivationResult) => {
    const msg = result.bonus_creditado
      ? `Pulseira activada! Bónus de ${fCVE(result.bonus_creditado)} creditado.`
      : 'Pulseira activada com sucesso!';
    showSuccess(msg);
    load();
  };

  const handleToggleBlock = async (w: Wristband) => {
    setBlocking(w.id);
    const action = w.status === 'ACTIVE' ? 'block' : 'unblock';
    try {
      const res = await api.post<{ success: boolean; error?: string }>(
        `/user-events/cashless/wristbands/${w.id}/block`,
        { action }
      );
      if (res.data.success) {
        showSuccess(action === 'block' ? 'Pulseira bloqueada' : 'Pulseira desbloqueada');
        load();
      } else {
        showError(res.data.error || 'Erro ao actualizar pulseira');
      }
    } catch (err: any) {
      showError(err?.response?.data?.error || 'Erro ao actualizar pulseira');
    } finally {
      setBlocking(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-pagali-gray-900 flex items-center gap-3">
            <FiWatch className="text-pink-500" /> Pulseira NFC
          </h1>
          <p className="text-sm text-pagali-gray-500 mt-1">
            Associa uma pulseira NFC à tua conta para pagamentos cashless em eventos e comerciantes Pagali Pay.
          </p>
        </div>
        <Button onClick={() => setActivateOpen(true)}>
          <FiPlus className="mr-1" /> Associar Pulseira
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-pink-50 border border-pink-200 rounded-2xl">
        <FiInfo className="text-pink-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-pink-800">
          <p className="font-semibold mb-1">Como funciona?</p>
          <p>A tua pulseira NFC está ligada directamente ao saldo da tua <strong>Wallet Pagali</strong>. Ao pagar num comerciante ou evento, o valor é debitado automaticamente.</p>
        </div>
      </div>

      {/* Wristbands list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoaderComponent size="lg" text="A carregar pulseiras..." />
        </div>
      ) : wristbands.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-pink-50 flex items-center justify-center mb-4">
              <FiWatch className="text-3xl text-pink-400" />
            </div>
            <h3 className="text-lg font-semibold text-pagali-gray-800 mb-2">Nenhuma pulseira associada</h3>
            <p className="text-pagali-gray-500 text-sm max-w-sm mb-6">
              Associa uma pulseira NFC à tua conta para poderes pagar em eventos e comerciantes Pagali Pay sem usar o telemóvel.
            </p>
            <Button onClick={() => setActivateOpen(true)}>
              <FiPlus className="mr-2" /> Associar Primeira Pulseira
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {wristbands.map(w => (
            <WristbandCard
              key={w.id}
              w={w}
              onToggleBlock={handleToggleBlock}
              blocking={blocking === w.id}
            />
          ))}
          {/* Add new */}
          <button
            onClick={() => setActivateOpen(true)}
            className="rounded-2xl border-2 border-dashed border-pink-200 flex flex-col items-center justify-center min-h-[220px] hover:border-pink-400 hover:bg-pink-50/50 transition-all group"
          >
            <FiPlus className="text-2xl text-pink-300 group-hover:text-pink-500 mb-2" />
            <span className="text-sm font-medium text-pink-300 group-hover:text-pink-500">
              Associar nova pulseira
            </span>
          </button>
        </div>
      )}

      <ActivateModal
        open={activateOpen}
        onClose={() => setActivateOpen(false)}
        onSuccess={handleActivated}
      />
    </div>
  );
};

export default Pulseira;
